package org.example.project.screens_shared

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.service.LoginService
import org.example.project.service.ProfilBildImportService
import org.example.project.service.YARD_PDF_LOGO
import org.example.project.service.YARD_PARKPLAETZE_BENUTZEN
import org.example.project.service.YARD_PALETTENTAUSCH_WA_AKTIV
import org.example.project.service.YARD_PALETTENTAUSCH_WE_AKTIV
import org.example.project.service.YardSettingsService
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.withYardSettingsDefaults
import org.example.project.service.yardSettingsBool
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.accept
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexWrap
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.gridTemplateColumns
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File

private const val YARD_SETTINGS_ROW_GRID = "260px 110px"
private const val YARD_SETTINGS_LOGO_ROW_GRID = "260px 170px"

@Composable
fun SHAREDYardSettings(@Suppress("UNUSED_PARAMETER") art: String = org.example.project.service.currentSaveArt()) {
    Style(SharedWorkspaceStyle)
    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current

    var settings by remember { mutableStateOf(YardSettingsService.defaultSettings()) }
    var logoFilePicker by remember { mutableStateOf<HTMLInputElement?>(null) }
    var pendingLogoFile by remember { mutableStateOf<File?>(null) }
    var pendingLogoPreview by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }
    var info by remember { mutableStateOf("") }

    suspend fun reloadSettings() {
        settings = YardSettingsService.load()
    }

    fun setYardSetting(key: String, value: Boolean) {
        settings = when (key) {
            YARD_PARKPLAETZE_BENUTZEN -> settings.copy(parkplaetzeImYardBenutzen = value)
            YARD_PALETTENTAUSCH_WE_AKTIV -> settings.copy(palettentauschWeAktiv = value)
            YARD_PALETTENTAUSCH_WA_AKTIV -> settings.copy(palettentauschWaAktiv = value)
            else -> settings
        }.withYardSettingsDefaults()
    }

    LaunchedEffect(Unit) {
        reloadSettings()
    }

    Div({ classes(SharedWorkspaceStyle.workspace) }) {
        Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
            Div({
                classes(SharedWorkspaceStyle.contentInner)
                style { maxWidth(100.percent); overflowY("auto"); property("flex", "1"); minHeight(0.px) }
            }) {
                if (internalTitleVisible) {
                    Div({ classes(SharedWorkspaceStyle.rightTitle) }) {
                        Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Yard Settings") }
                    }
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        gap(22.px)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    LumaticaButton(
                        label = "Speichern",
                        onClick = {
                            scope.launch {
                                saving = true
                                val file = pendingLogoFile
                                var nextSettings = settings
                                if (file != null) {
                                    val currentUser = LoginService.getUser()
                                    if (currentUser == null) {
                                        info = "Kein Benutzer angemeldet."
                                        saving = false
                                        return@launch
                                    }
                                    ProfilBildImportService.loadProfileImages(currentUser)
                                        .mapNotNull { it.file.takeIf(String::isNotBlank) }
                                        .filter { it.startsWith("yard_logo.") }
                                        .forEach { ProfilBildImportService.deleteProfileImage(currentUser, it) }
                                    val fileName = yardLogoFileName(file)
                                    val uploadOk = ProfilBildImportService.uploadProfileImage(currentUser, file, fileName)
                                    if (!uploadOk) {
                                        info = "Logo konnte nicht gespeichert werden."
                                        saving = false
                                        return@launch
                                    }
                                    val uploadedLogo = ProfilBildImportService.loadProfileImages(currentUser)
                                        .firstOrNull { it.file == fileName }
                                    val logoUrl = uploadedLogo?.yardImageUrl().orEmpty()
                                    if (logoUrl.isNotBlank()) {
                                        nextSettings = settings.copy(logoSrc = logoUrl).withYardSettingsDefaults()
                                        settings = nextSettings
                                        pendingLogoFile = null
                                        pendingLogoPreview = ""
                                    }
                                }
                                val ok = YardSettingsService.save(nextSettings)
                                info = if (ok) "Gespeichert." else "Speichern fehlgeschlagen."
                                saving = false
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        enabled = !saving,
                        heightPx = 42
                    )
                    if (info.isNotBlank()) {
                        Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text(info) }
                    }
                }

                Div({ classes(SharedWorkspaceStyle.centeredDivider) })

                Div({ classes(SharedWorkspaceStyle.columnList) }) {
                    Div({ classes(SharedWorkspaceStyle.permissionColumn) }) {
                        YardLogoPreview(pendingLogoPreview.ifBlank { settings.logoSrc })

                        Input(InputType.File, attrs = {
                            accept(".png,.jpg,.jpeg,.webp")
                            ref { element ->
                                logoFilePicker = element
                                onDispose { logoFilePicker = null }
                            }
                            style { display(DisplayStyle.None) }
                            onInput {
                                val file = (it.target as? HTMLInputElement)?.files?.item(0) ?: return@onInput
                                pendingLogoFile = file
                                pendingLogoPreview = window.asDynamic().URL.createObjectURL(file) as String
                                info = ""
                            }
                        })

                        YardLogoSettingRow(
                            onSelect = {
                                logoFilePicker?.value = ""
                                logoFilePicker?.click()
                            }
                        )

                        YardSettingsCheckboxRow(
                            label = "Palettentausch WE",
                            checked = settings.yardSettingsBool(YARD_PALETTENTAUSCH_WE_AKTIV, true),
                            onChange = { setYardSetting(YARD_PALETTENTAUSCH_WE_AKTIV, it) }
                        )

                        YardSettingsCheckboxRow(
                            label = "Palettentausch WA",
                            checked = settings.yardSettingsBool(YARD_PALETTENTAUSCH_WA_AKTIV, true),
                            onChange = { setYardSetting(YARD_PALETTENTAUSCH_WA_AKTIV, it) }
                        )

                        YardSettingsCheckboxRow(
                            label = "Parkplätze im Yard benutzen",
                            checked = settings.yardSettingsBool(YARD_PARKPLAETZE_BENUTZEN, true),
                            onChange = { setYardSetting(YARD_PARKPLAETZE_BENUTZEN, it) }
                        )
                    }
                }
            }
        }
    }
}

private fun yardLogoFileName(file: File): String {
    val extension = file.name.substringAfterLast('.', "")
        .lowercase()
        .takeIf { it in setOf("png", "jpg", "jpeg", "webp") }
        ?: "png"
    return "yard_logo.$extension"
}

private fun All.yardImageUrl(): String =
    openUrl.ifBlank { downloadUrl }.ifBlank { logoSrc }

@Composable
private fun YardSettingsCheckboxRow(
    label: String,
    checked: Boolean,
    onChange: (Boolean) -> Unit
) {
    Div({
        classes(SharedWorkspaceStyle.permissionRow)
        style {
            gridTemplateColumns(YARD_SETTINGS_ROW_GRID)
        }
    }) {
        Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text(label) }
        LumaticaCheckbox(checked = checked, onChange = onChange)
    }
}

@Composable
private fun YardLogoPreview(logoSrc: String) {
    Div({
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            minHeight(82.px)
        }
    }) {
        if (logoSrc.isNotBlank()) {
            Img(src = logoSrc) {
                style {
                    maxWidth(240.px)
                    height(72.px)
                    property("object-fit", "contain")
                }
            }
        }
    }
}

@Composable
private fun YardLogoSettingRow(onSelect: () -> Unit) {
    Div({
        classes(SharedWorkspaceStyle.permissionRow)
        style {
            gridTemplateColumns(YARD_SETTINGS_LOGO_ROW_GRID)
        }
    }) {
        Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text("FirmenLogo") }
        LumaticaButton(
            label = "Einstellen",
            onClick = onSelect,
            variant = LumaticaButtonVariant.Standard,
            heightPx = 36
        )
    }
}
